import axios from "axios";
import { useEffect, useState } from "react";
import { FaFileDownload } from "react-icons/fa";
import { MdFilterAltOff } from "react-icons/md";
import * as XLSX from "xlsx";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./ResultComponent.module.css";

const API_BASE_URL =
  "https://prod.toxboxai.com/api";

const Modal = ({
  isOpen,
  onClose,
  title,
  summary,
  agentName,
  onSave,
  rowKey,
}) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");

  const userID = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!isOpen) return;

    const fetchProgress = async () => {
      if (!userID || !token) {
        alert("Please log in to view progress.");
        onClose();
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/progress`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setTableData(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Error fetching progress:", error);
        setTableData([{ agentName, status: summary }]); // Fallback to props
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [isOpen, agentName, summary, userID, token]);

  if (!isOpen) return null;

  const handleAddRow = async () => {
    if (!newFieldName || !newFieldValue || !userID || !token) {
      alert("Please fill in all fields and ensure you are logged in.");
      return;
    }

    try {
      const newRow = {
        agentName: newFieldName,
        status: newFieldValue,
        userId: userID,
      };
      const response = await axios.post(`${API_BASE_URL}/progress`, newRow, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && typeof response.data === "object") {
        setTableData([...tableData, response.data]);
        setNewFieldName("");
        setNewFieldValue("");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error saving new row:", error);
      alert("Please try again.");
    }
  };

  const handleSave = () => {
    onSave(tableData, rowKey);
    onClose();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>{title}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.group}>
            <input
              type="text"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              placeholder="New field name"
              className={styles.rowInput}
            />
            <input
              type="text"
              value={newFieldValue}
              onChange={(e) => setNewFieldValue(e.target.value)}
              placeholder="New field value"
              className={styles.rowInput}
            />
            <button onClick={handleAddRow} className={styles.saveBtn}>
              Add Row
            </button>
          </div>

          {loading ? (
            <LoaderSpiner />
          ) : tableData.length === 0 ? (
            <p>No progress data available.</p>
          ) : (
            <table className={styles.summaryTable}>
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.agentName || "Unknown"}</td>
                    <td>{row.status || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={styles.modalFooter}>
          <button onClick={handleSave} className={styles.saveBtn}>
            Save
          </button>
          <button onClick={onClose} className={styles.closeBtn}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ResultComponent = () => {
  const [firstDropdown, setFirstDropdown] = useState("inbound");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    summary: "",
    agentName: "",
    key: "",
  });
  const [loanData, setLoanData] = useState([]);
  const [filteredLoanData, setFilteredLoanData] = useState([]);
  const [isLoanDataLoading, setIsLoanDataLoading] = useState(false);
  const [loanDataError, setLoanDataError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState("");
  const [timeFilter, setTimeFilter] = useState("");
  const [interestsFilter, setInterestsFilter] = useState("all");
  const rowsPerPage = 6;
  const userID = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!userID || !token) {
      setAgentError("Please log in to access this feature.");
      return;
    }

    const fetchAgentData = async () => {
      try {
        setIsAgentLoading(true);
        setAgentError(null);
        const response = await axios.get(
          firstDropdown === "inbound"
            ? `${API_BASE_URL}/agent/getbyid/${userID}`
            : `${API_BASE_URL}/new/agent/user/${userID}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("API Response:", response.data); // Debug log to inspect response
        let fetchedAgents = [];
        if (response.data.success && Array.isArray(response.data.data)) {
          fetchedAgents = response.data.data; // Handle { success: true, data: [...] }
        } else if (Array.isArray(response.data)) {
          fetchedAgents = response.data; // Handle direct array
        } else if (
          response.data.agents &&
          Array.isArray(response.data.agents)
        ) {
          fetchedAgents = response.data.agents; // Handle { agents: [...] }
        } else if (response.data.agent && !Array.isArray(response.data.agent)) {
          fetchedAgents = [response.data.agent]; // Handle { agent: {...} }
        } else {
          throw new Error("Unexpected response structure");
        }
        setAgents(fetchedAgents);
        setSelectedAgent(fetchedAgents.length > 0 ? fetchedAgents[0]._id : "");
      } catch (error) {
        console.error("Error fetching agents:", error);
        setAgentError(`Failed to load agents: ${error.message}`);
        setAgents([]);
      } finally {
        setIsAgentLoading(false);
      }
    };

    fetchAgentData();
  }, [firstDropdown, userID, token]);

  useEffect(() => {
    if (!selectedAgent || !userID || !token) return;

    const fetchLoanData = async () => {
      const agentObj = agents.find((a) => a._id === selectedAgent);
      const phoneNumber = agentObj?.phoneNumber || "";
      console.log("Selected Agent Phone Number:", phoneNumber);
      try {
        setIsLoanDataLoading(true);
        setLoanDataError(null);
        const response = await axios.get(
          `${API_BASE_URL}/loan-lead-reports/${phoneNumber.replace("+", "")}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success && Array.isArray(response.data.data)) {
          setLoanData(response.data.data);
          setFilteredLoanData(response.data.data);
        } else {
          setLoanData([]);
          setFilteredLoanData([]);
          setLoanDataError("Invalid loan data format.");
        }
      } catch (error) {
        console.error("Error fetching loan data:", error);
        setLoanDataError("Failed to load loan data. Please try again.");
        setLoanData([]);
        setFilteredLoanData([]);
      } finally {
        setIsLoanDataLoading(false);
      }
    };

    fetchLoanData();
  }, [selectedAgent, userID, token]);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...loanData];

      if (dateFilter) {
        const filterDate = new Date(dateFilter);
        filtered = filtered.filter((row) => {
          const modifiedDate = new Date(row.lastModified);
          return (
            modifiedDate.getFullYear() === filterDate.getFullYear() &&
            modifiedDate.getMonth() === filterDate.getMonth() &&
            modifiedDate.getDate() === filterDate.getDate()
          );
        });
      }

      if (timeFilter) {
        const [filterHours, filterMinutes] = timeFilter.split(":").map(Number);
        filtered = filtered.filter((row) => {
          const modifiedDate = new Date(row.lastModified);
          return (
            modifiedDate.getHours() === filterHours &&
            modifiedDate.getMinutes() === filterMinutes
          );
        });
      }

      if (interestsFilter !== "all") {
        filtered = filtered.filter((row) => {
          const interests = row.content?.interests;
          if (typeof interests === "string") {
            return interests.toLowerCase() === interestsFilter;
          } else if (typeof interests === "boolean") {
            return interests === (interestsFilter === "yes");
          } else if (Array.isArray(interests)) {
            return interests.some((i) => i.toLowerCase() === interestsFilter);
          }
          return interestsFilter === "no";
        });
      }

      setFilteredLoanData(filtered);
      setCurrentPage(1);
    };

    applyFilters();
  }, [loanData, dateFilter, timeFilter, interestsFilter]);

  const handleClearFilters = () => {
    setDateFilter("");
    setTimeFilter("");
    setInterestsFilter("all");
    setCurrentPage(1);
  };

  const extractCustomerName = (summary) => {
    if (!summary) return "Unknown";
    const match = summary.match(/The customer,\s*([^,.]+)/);
    return match ? match[1].trim() : "Unknown";
  };

  const handleViewSummary = (row) => {
    const agent = agents.find((a) => a._id === selectedAgent);
    const agentName = agent
      ? agent.name || `Agent ${agent._id}`
      : "Unknown Agent";
    setModalContent({
      title: `Summary for ${extractCustomerName(row.content.summary || "")}`,
      summary: row.content.summary || "",
      agentName,
      key: row.key,
    });
    setModalOpen(true);
  };

  const handleSaveModal = (updatedData, rowKey) => {
    if (!updatedData.length) return;

    const updatedSummary = updatedData[updatedData.length - 1]?.status || "";
    setLoanData((prevData) =>
      prevData.map((row) =>
        row.key === rowKey
          ? { ...row, content: { ...row.content, summary: updatedSummary } }
          : row
      )
    );
    setFilteredLoanData((prevData) =>
      prevData.map((row) =>
        row.key === rowKey
          ? { ...row, content: { ...row.content, summary: updatedSummary } }
          : row
      )
    );
  };

  const handleDownloadExcel = () => {
    if (filteredLoanData.length === 0) {
      alert("No data to export.");
      return;
    }

    const dataToExport = filteredLoanData.map((row) => {
      const content = row.content || {};
      return {
        "Last Modified": new Date(row.lastModified).toLocaleString(),
        ...Object.keys(content).reduce((acc, key) => {
          acc[formatColumnName(key)] = formatFieldValue(content[key]);
          return acc;
        }, {}),
        "Customer Name": extractCustomerName(content.summary || ""),
        Summary: content.summary || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Lead Reports");

    const colWidths = Object.keys(dataToExport[0]).map((key) => ({
      wch: Math.max(
        key.length,
        ...dataToExport.map((row) => String(row[key] || "").length)
      ),
    }));
    worksheet["!cols"] = colWidths;

    XLSX.writeFile(workbook, "loan_lead_reports.xlsx");
  };

  const totalPages = Math.ceil(filteredLoanData.length / rowsPerPage);
  const paginatedData = filteredLoanData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const columns =
    loanData.length > 0
      ? ["lastModified", ...Object.keys(loanData[0].content), "customerName"]
      : [];

  const formatColumnName = (key) => {
    if (key === "customerName") return "Customer Name";
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace("Interests", "Interests");
  };

  const formatFieldValue = (value) => {
    if (value == null) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value))
      return value.length > 0 ? value.join(", ") : "None";
    return String(value);
  };

  return (
    <div className={styles.resultContainer}>
      <div className={styles.group}>
        <div className={styles.dropdownGroup}>
          <label htmlFor="type-select" className={styles.label}>
            Loan Type
          </label>
          <select
            id="type-select"
            value={firstDropdown}
            onChange={(e) => setFirstDropdown(e.target.value)}
            className={styles.select}
          >
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
        </div>

        <div className={styles.dropdownGroup}>
          <label htmlFor="agent-select" className={styles.label}>
            Agent
          </label>
          <select
            id="agent-select"
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            disabled={isAgentLoading || agentError || agents.length === 0}
            className={styles.select}
          >
            <option value="">Select an agent</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name || `Agent ${agent._id}`}
              </option>
            ))}
          </select>
          {isAgentLoading && (
            <p className={styles.loadingText}>Loading agents...</p>
          )}
          {agentError && <p className={styles.errorText}>{agentError}</p>}
        </div>
      </div>
      <hr />

      {isLoanDataLoading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.GroupContainer}>
            <div className={styles.filterGroup}>
              <div className={styles.filterItem}>
                <label htmlFor="date-filter" className={styles.label}>
                  Date
                </label>
                <input
                  type="date"
                  id="date-filter"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className={styles.filterInput}
                />
              </div>
              <div className={styles.filterItem}>
                <label htmlFor="time-filter" className={styles.label}>
                  Time
                </label>
                <input
                  type="time"
                  id="time-filter"
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className={styles.filterInput}
                />
              </div>
              <div className={styles.filterItem}>
                <label htmlFor="interests-filter" className={styles.label}>
                  Interests
                </label>
                <select
                  id="interests-filter"
                  value={interestsFilter}
                  onChange={(e) => setInterestsFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div className={styles.buttonGroup}>
              <button
                onClick={handleClearFilters}
                className={styles.clearBtn}
                title="Click to clear all filters"
              >
                <MdFilterAltOff />
              </button>
              <button
                onClick={handleDownloadExcel}
                className={styles.downloadBtn}
                title="Click to download the result in excel"
              >
                <FaFileDownload />
              </button>
            </div>
          </div>

          {loanDataError && <p className={styles.errorText}>{loanDataError}</p>}
          {!isLoanDataLoading &&
            !loanDataError &&
            filteredLoanData.length === 0 && (
              <p className={styles.noDataText}>No data available.</p>
            )}
          <div className={styles.dataTableContainer}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>View</th>
                  {columns.map((col) => (
                    <th key={col}>{formatColumnName(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <button
                        onClick={() => handleViewSummary(row)}
                        className={styles.viewBtn}
                      >
                        View
                      </button>
                    </td>
                    <td>{new Date(row.lastModified).toLocaleString()}</td>
                    {Object.keys(row.content).map((col) => (
                      <td key={col}>{formatFieldValue(row.content[col])}</td>
                    ))}
                    <td>{extractCustomerName(row.content.summary || "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLoanData.length > 0 && (
            <div className={styles.pagination}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={styles.pageButton}
              >
                Previous
              </button>
              {[...Array(totalPages).keys()].map((page) => (
                <button
                  key={page + 1}
                  onClick={() => handlePageChange(page + 1)}
                  className={`${styles.pageButton} ${currentPage === page + 1 ? styles.activePage : ""
                    }`}
                >
                  {page + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={styles.pageButton}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        summary={modalContent.summary}
        agentName={modalContent.agentName}
        onSave={handleSaveModal}
        rowKey={modalContent.key}
      />
    </div>
  );
};

export default ResultComponent;
